import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable, ObservableMap } from "mobx";
import { api } from "@/modules/api";
import type { ApiSchema } from "@/modules/api";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { SmartViewKind } from "@/domains/smart/constants";
import { resolveSmartContextId } from "./SmartContextId";
import { HeadsUpOperationKind, isContextOperation } from "./HeadsUpOperation";
import {
  HeadsUpAutoGenerator,
  HeadsUpEditorBlurPayload,
  HeadsUpEditorUpdatePayload,
} from "./HeadsUpAutoGenerator";
import { buildSmartViewPayload } from "./impl/build-smart-view-payload";
import { runDedupedViewOperation } from "./impl/run-deduped-view-operation";

export interface SmartContextRequest {
  activeNoteId?: string | null;
  query?: string | null;
  smartViewKind?: SmartViewKind | null;
}

type HeadsUpInFlightEntry = {
  kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
  promise: Promise<ApiSchema["SmartViewResponseSchema"] | null>;
  smartViewId?: string | null;
};

export class AppStoreHeadsUpStore extends AppSubStore {
  operationByContextId = new ObservableMap<string, HeadsUpOperationKind>();
  operationByViewId = new ObservableMap<string, HeadsUpOperationKind>();
  errorByContextId = new ObservableMap<string, string | null>();
  activeContextId: string | null = null;
  autoGenerationDeferredByContextId = new ObservableMap<string, boolean>();
  private inFlightByContextId = new Map<string, HeadsUpInFlightEntry>();
  private inFlightByViewId = new Map<string, HeadsUpInFlightEntry>();
  private readonly autoGenerator: HeadsUpAutoGenerator;
  findMoreByViewId = new ObservableMap<string, boolean>();
  private inFlightFindMoreByViewId = new Map<
    string,
    Promise<ApiSchema["SmartViewResponseSchema"] | null>
  >();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.autoGenerator = new HeadsUpAutoGenerator(this);

    makeObservable<
      this,
      | "resolveContextIdFrom"
      | "persistFromResponse"
      | "runOperation"
      | "inFlightByContextId"
      | "inFlightByViewId"
      | "inFlightFindMoreByViewId"
      | "setOperationState"
      | "clearOperationState"
      | "setAutoGenerationDeferredState"
      | "autoGenerator"
    >(this, {
      hydrateContext: true,
      operationByContextId: observable,
      operationByViewId: observable,
      errorByContextId: observable,
      activeContextId: observable,
      autoGenerationDeferredByContextId: observable,
      autoGenerator: false,
      inFlightByContextId: false,
      inFlightByViewId: false,
      findMoreByViewId: observable,
      inFlightFindMoreByViewId: false,
      anyGenerating: computed,
      isHydratingForContextId: false,
      isGeneratingForContextId: false,
      isRefreshingForViewId: false,
      isRegeneratingForViewId: false,
      isFindingMoreForViewId: false,
      isBusyForContextId: false,
      getErrorForContextId: false,
      setActiveContextId: action,
      setOperationState: action,
      clearOperationState: action,
      generateForContext: action,
      refreshSmartView: action,
      regenerateSmartView: action,
      deferAutoGenerationUntilNoteReady: action,
      markNoteSyncedForAutoGeneration: action,
      markNoteHasPrimaryLabelForAutoGeneration: action,
      handleEditorUpdate: action,
      handleEditorBlur: action,
      canAutoGenerateForContextId: false,
      findMoreForSmartView: action,
      resolveContextIdFrom: false,
      runOperation: false,
      persistFromResponse: false,
      setAutoGenerationDeferredState: action,
      resolveContextIdForRequest: false,
      isAutoGenerationReadyForContextId: false,
    });
  }

  get anyGenerating(): boolean {
    for (const value of this.operationByContextId.values()) {
      if (value === HeadsUpOperationKind.Generate) return true;
    }
    return false;
  }

  isGeneratingForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return this.operationByContextId.get(contextId) === HeadsUpOperationKind.Generate;
  };

  isHydratingForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return this.operationByContextId.get(contextId) === HeadsUpOperationKind.Hydrate;
  };

  isRefreshingForViewId = (viewId: string | null): boolean => {
    if (!viewId) return false;
    return this.operationByViewId.get(viewId) === HeadsUpOperationKind.Refresh;
  };

  isRegeneratingForViewId = (viewId: string | null): boolean => {
    if (!viewId) return false;
    return this.operationByViewId.get(viewId) === HeadsUpOperationKind.Regenerate;
  };

  isFindingMoreForViewId = (viewId: string | null): boolean => {
    if (!viewId) return false;
    return this.findMoreByViewId.get(viewId) === true;
  };

  isBusyForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return isContextOperation(this.operationByContextId.get(contextId));
  };

  getErrorForContextId = (contextId: string | null): string | null => {
    if (!contextId) return null;
    if (this.isBusyForContextId(contextId)) return null;
    return this.errorByContextId.get(contextId) ?? null;
  };

  setActiveContextId = (contextId: string | null) => {
    this.activeContextId = contextId;
  };

  canAutoGenerateForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return this.isAutoGenerationReadyForContextId(contextId);
  };

  deferAutoGenerationUntilNoteReady = (noteId: string | null) => {
    if (!noteId) return;
    this.autoGenerator.deferAutoGenerationFor({
      activeNoteId: noteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  };

  markNoteSyncedForAutoGeneration = (noteId: string | null) => {
    if (!noteId) return;
    this.autoGenerator.markNoteSynced({
      activeNoteId: noteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  };

  markNoteHasPrimaryLabelForAutoGeneration = (noteId: string | null) => {
    if (!noteId) return;
    this.autoGenerator.markNoteHasPrimaryLabel({
      activeNoteId: noteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  };

  handleEditorUpdate = (payload: HeadsUpEditorUpdatePayload) => {
    this.autoGenerator.handleEditorUpdate(payload);
  };

  handleEditorBlur = (payload: HeadsUpEditorBlurPayload) => {
    this.autoGenerator.handleEditorBlur(payload);
  };

  resolveContextIdForRequest = (request: SmartContextRequest): string | null => {
    return this.resolveContextIdFrom(request);
  };

  isAutoGenerationReadyForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return !this.autoGenerationDeferredByContextId.has(contextId);
  };

  setAutoGenerationDeferredState(contextId: string | null, shouldDefer: boolean) {
    if (!contextId) return;

    if (shouldDefer) {
      this.autoGenerationDeferredByContextId.set(contextId, true);
    } else {
      this.autoGenerationDeferredByContextId.delete(contextId);
    }
  }

  private setOperationState({
    contextId,
    smartViewId,
    kind,
  }: {
    contextId: string | null;
    smartViewId: string | null;
    kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
  }) {
    if (contextId) {
      this.operationByContextId.set(contextId, kind);
    }

    if (smartViewId) {
      this.operationByViewId.set(smartViewId, kind);
    }
  }

  private clearOperationState({
    contextId,
    smartViewId,
  }: {
    contextId: string | null;
    smartViewId: string | null;
  }) {
    if (contextId) {
      this.operationByContextId.delete(contextId);
      this.inFlightByContextId.delete(contextId);
    }

    if (smartViewId) {
      this.operationByViewId.delete(smartViewId);
      this.inFlightByViewId.delete(smartViewId);
    }
  }

  private runOperation({
    contextId,
    smartViewId,
    kind,
    executor,
  }: {
    contextId: string | null;
    smartViewId: string | null;
    kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
    executor: () => Promise<ApiSchema["SmartViewResponseSchema"] | null>;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedContextId = contextId ?? null;
    const normalizedViewId = smartViewId ?? null;

    if (normalizedContextId) {
      const existingContextEntry = this.inFlightByContextId.get(normalizedContextId);
      if (existingContextEntry) {
        return existingContextEntry.promise;
      }
    }

    if (normalizedViewId) {
      const existingViewEntry = this.inFlightByViewId.get(normalizedViewId);
      if (existingViewEntry) {
        return existingViewEntry.promise;
      }
    }

    const promise = (async () => {
      this.setOperationState({
        contextId: normalizedContextId,
        smartViewId: normalizedViewId,
        kind,
      });

      try {
        return await executor();
      } finally {
        this.clearOperationState({
          contextId: normalizedContextId,
          smartViewId: normalizedViewId,
        });
      }
    })();

    const entry: HeadsUpInFlightEntry = {
      kind,
      promise,
      smartViewId: normalizedViewId,
    };

    if (normalizedContextId) {
      this.inFlightByContextId.set(normalizedContextId, entry);
    }

    if (normalizedViewId) {
      this.inFlightByViewId.set(normalizedViewId, entry);
    }

    return promise;
  }

  private resolveContextIdFrom({
    activeNoteId = null,
    query = null,
    smartViewKind = SmartViewKind.HeadsUp,
  }: SmartContextRequest): string | null {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
    return resolveSmartContextId({
      spaceAccountId,
      activeNoteId: activeNoteId ?? null,
      query: query ?? null,
      smartViewKind: smartViewKind ?? SmartViewKind.HeadsUp,
    });
  }

  async hydrateContext({
    activeNoteId = null,
    query = null,
    smartViewKind = SmartViewKind.HeadsUp,
  }: SmartContextRequest): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const contextId = this.resolveContextIdFrom({ activeNoteId, query, smartViewKind });
    if (!contextId) return null;

    return this.runOperation({
      contextId,
      smartViewId: null,
      kind: HeadsUpOperationKind.Hydrate,
      executor: async () => {
        try {
          const response = await api.get("/v2/smart-views/by-context/{smart_context_id}", {
            params: {
              path: {
                smart_context_id: contextId,
              },
            },
          });
          const data = response.data;
          if (!data) {
            return null;
          }

          const payloadJson = buildSmartViewPayload({
            spaceAccountId:
              this.store.spaceAccounts.myPersonalSpaceAccountId ?? data.associated_space_account_id,
            smartViewKind,
            activeNoteId,
            query,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });

          return data;
        } catch (_error) {
          logger.debug({
            message: "[HeadsUp] Hydration request failed; falling back to generation",
            info: { contextId },
          });
          return null;
        }
      },
    });
  }

  async generateForContext({
    activeNoteId = null,
    query = null,
    smartViewKind = SmartViewKind.HeadsUp,
  }: SmartContextRequest): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
    if (!spaceAccountId) {
      logger.warn({
        message: "[HeadsUp] Cannot generate without space account",
      });
      return null;
    }

    const contextId = this.resolveContextIdFrom({ activeNoteId, query, smartViewKind });
    if (!contextId) return null;

    this.errorByContextId.delete(contextId);

    return this.runOperation({
      contextId,
      smartViewId: null,
      kind: HeadsUpOperationKind.Generate,
      executor: async () => {
        try {
          const response = await api.post(
            "/v2/smart-views/by-context/{smart_context_id}/generate",
            {
              params: {
                path: {
                  smart_context_id: contextId,
                },
              },
              body: {
                context: {
                  space_account_id: spaceAccountId,
                  smart_view_kind: smartViewKind,
                  ...(activeNoteId ? { active_note_id: activeNoteId } : {}),
                  ...(query ? { query } : {}),
                },
              },
            }
          );

          const data = response.data;

          if (!data) {
            throw new Error("Failed to generate smart view");
          }

          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind,
            activeNoteId,
            query,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: false });

          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          this.errorByContextId.set(contextId, message);
          throw error;
        }
      },
    });
  }

  protected async persistFromResponse({
    data,
    payloadJson,
    replaceCards,
  }: {
    data: ApiSchema["SmartViewResponseSchema"];
    payloadJson: Record<string, unknown>;
    replaceCards: boolean;
  }): Promise<void> {
    const smartContextId = data.smart_context_id;

    if (this.store.database.isInitialized()) {
      try {
        logger.debug({
          message: "[HeadsUp] Upserting smart context",
          info: { smartContextId },
        });

        await this.store.database.smartContexts.upsertContext({
          id: smartContextId,
          associated_space_account_id: data.associated_space_account_id,
          payload_json: payloadJson,
          created_at: data.created_at,
          updated_at: data.updated_at,
        });

        logger.debug({
          message: "[HeadsUp] Upserting smart view",
          info: { smartViewId: data.id, smartContextId },
        });

        const responseRefreshedAt = data.refreshed_at ?? null;

        await this.store.database.smartViews.upsertView({
          id: data.id,
          associated_space_account_id: data.associated_space_account_id,
          smart_context_id: data.smart_context_id,
          derived_kind: null,
          derived_internal_label: null,
          value_json: data.value_json,
          has_found_more: (data as { has_found_more?: boolean }).has_found_more ?? false,
          refreshed_at: responseRefreshedAt,
          created_at: data.created_at ?? null,
          updated_at: data.updated_at ?? null,
        });

        const smartCards = data.smart_cards ?? [];
        const cardTimestamp = responseRefreshedAt ?? new Date().toISOString();

        if (replaceCards) {
          await this.store.database.smartCards.replaceCardsForSmartView({
            smartViewId: data.id,
            rows: smartCards.map(card => {
              const cardValue = (card as { value?: Record<string, unknown> | null }).value ?? {};
              const rawTitle = (cardValue as { title?: unknown }).title;
              const derivedInternalLabel =
                (typeof rawTitle === "string" && rawTitle.trim() ? rawTitle : null) ?? card.kind;
              const sortKey = (card as { sort_key?: string | null }).sort_key ?? null;
              return {
                id: (card as { id: string }).id,
                associated_space_account_id: data.associated_space_account_id,
                smart_context_id: data.smart_context_id,
                smart_view_id: (card as { smart_view_id: string }).smart_view_id,
                sort_key: sortKey,
                added_by_space_account_id: null,
                derived_kind: (card as { kind: string | null }).kind ?? null,
                derived_internal_label: derivedInternalLabel,
                originated_from_find_more:
                  (card as { originated_from_find_more?: boolean | null })
                    .originated_from_find_more ?? false,
                value_json: cardValue,
                refreshed_at: cardTimestamp,
                created_at: cardTimestamp,
                updated_at: cardTimestamp,
              };
            }),
          });
        } else {
          for (const card of smartCards) {
            const cardValue = (card as { value?: Record<string, unknown> | null }).value ?? {};
            const rawTitle = (cardValue as { title?: unknown }).title;
            const derivedInternalLabel =
              (typeof rawTitle === "string" && rawTitle.trim() ? rawTitle : null) ?? card.kind;
            const sortKey = (card as { sort_key?: string | null }).sort_key ?? null;

            await this.store.database.smartCards.upsertCard({
              id: (card as { id: string }).id,
              associated_space_account_id: data.associated_space_account_id,
              smart_context_id: data.smart_context_id,
              smart_view_id: (card as { smart_view_id: string }).smart_view_id,
              sort_key: sortKey,
              added_by_space_account_id: null,
              derived_kind: (card as { kind: string | null }).kind ?? null,
              derived_internal_label: derivedInternalLabel,
              originated_from_find_more:
                (card as { originated_from_find_more?: boolean | null })
                  .originated_from_find_more ?? false,
              value_json: cardValue,
              refreshed_at: cardTimestamp,
              created_at: cardTimestamp,
              updated_at: cardTimestamp,
            });
          }
        }

        logger.debug({
          message: "[HeadsUp] Finished upserting smart assets",
          info: {
            smartContextId,
            smartViewId: data.id,
            smartCardCount: smartCards.length,
          },
        });
      } catch (unknownErr) {
        const err = unknownErr as Error;
        logger.warn({
          message: "[HeadsUp] Local upsert failed; relying on sync",
          info: { error: err.message, smartContextId, smartViewId: data.id },
        });
      }
    } else {
      logger.warn({
        message: "[HeadsUp] Skipping local upsert; database not initialized",
        info: { smartContextId },
      });
    }

    const activeNoteId = (payloadJson["active_note_id"] as string | undefined) ?? null;
    if (activeNoteId) {
      this.autoGenerator.noteHasHeadsUp({
        activeNoteId,
        smartViewKind: SmartViewKind.HeadsUp,
      });
    }
  }

  async refreshSmartView({
    smartViewId,
    contextId,
  }: {
    smartViewId: string;
    contextId: string | null;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedContextId = contextId ?? null;
    if (normalizedContextId) {
      this.errorByContextId.delete(normalizedContextId);
    }

    return this.runOperation({
      contextId: normalizedContextId,
      smartViewId,
      kind: HeadsUpOperationKind.Refresh,
      executor: async () => {
        try {
          const response = await api.post("/v2/smart-views/{smart_view_id}/refresh", {
            params: {
              path: { smart_view_id: smartViewId },
            },
          });

          const data = response.data;
          if (!data) return null;

          const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: null,
            activeNoteId: null,
            query: null,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });
          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          toastModule.triggerToast({ content: message });
          throw error;
        }
      },
    });
  }

  async regenerateSmartView({
    smartViewId,
    contextId,
  }: {
    smartViewId: string;
    contextId: string | null;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedContextId = contextId ?? null;
    if (normalizedContextId) {
      this.errorByContextId.delete(normalizedContextId);
    }

    return this.runOperation({
      contextId: normalizedContextId,
      smartViewId,
      kind: HeadsUpOperationKind.Regenerate,
      executor: async () => {
        try {
          const response = await api.post("/v2/smart-views/{smart_view_id}/regenerate", {
            params: {
              path: { smart_view_id: smartViewId },
            },
          });

          const data = response.data;
          if (!data) return null;

          const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: null,
            activeNoteId: null,
            query: null,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });
          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          if (normalizedContextId) this.errorByContextId.set(normalizedContextId, message);
          throw error;
        }
      },
    });
  }

  async findMoreForSmartView({
    smartViewId,
    contextId,
  }: {
    smartViewId: string;
    contextId: string | null;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedViewId = smartViewId;
    const normalizedContextId = contextId ?? null;

    if (!normalizedViewId) {
      return null;
    }

    const promise = runDedupedViewOperation({
      viewId: normalizedViewId,
      inFlightMap: this.inFlightFindMoreByViewId,
      onStart: viewId => {
        this.findMoreByViewId.set(viewId, true);
      },
      onFinally: viewId => {
        this.findMoreByViewId.delete(viewId);
      },
      executor: async () => {
        try {
          const response = await api.post("/v2/smart-views/{smart_view_id}/find-more", {
            params: {
              path: { smart_view_id: normalizedViewId },
            },
          });

          const data = response.data;
          if (!data) return null;

          const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: null,
            activeNoteId: null,
            query: null,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });

          if (normalizedContextId) {
            this.errorByContextId.delete(normalizedContextId);
          }

          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          toastModule.triggerToast({ content: message });
          throw error;
        }
      },
    });

    return promise;
  }
}
